<template>
  <div
    v-if="show"
    class="ddt-backdrop"
    @click.self="$emit('update:show', false)"
  >
    <div class="ddt-modal">
      <button
        class="icons-button ddt-modal__close"
        type="button"
        @click="$emit('update:show', false)"
      >
        <i class="fa-solid fa-xmark"></i>
      </button>

      <h3 class="ddt-modal__title">Genera DDT</h3>
      <p class="ddt-modal__subtitle" v-if="clientName">
        Committente: <strong>{{ clientName }}</strong>
      </p>

      <!-- Selezione causale -->
      <div class="ddt-field">
        <label class="ddt-label">Causale *</label>
        <div class="ddt-causale-cards">
          <button
            v-for="opt in CAUSALI"
            :key="opt.value"
            type="button"
            class="ddt-causale-card"
            :class="{
              selected: causale === opt.value,
              'ddt-causale-card--disabled': isCausaleDisabled(opt.value),
            }"
            :disabled="isCausaleDisabled(opt.value)"
            :title="causaleDisabledTitle(opt.value)"
            @click="causale = opt.value"
          >
            <i :class="opt.icon"></i>
            <span>{{ opt.label }}</span>
          </button>
        </div>
      </div>

      <!-- Selezione operatori -->
      <div class="ddt-operators-row">
        <div class="ddt-field">
          <label class="ddt-label">Magazziniere</label>
          <select v-model="magazziniereId" class="ddt-select">
            <option :value="null">— Seleziona —</option>
            <option
              v-for="op in warehouseOperators"
              :key="op.id"
              :value="op.id"
            >
              {{ op.operatorName || op.email }}
            </option>
          </select>
        </div>
        <div class="ddt-field">
          <label class="ddt-label">Operatore</label>
          <select v-model="operatoreId" class="ddt-select">
            <option :value="null">— Seleziona —</option>
            <option v-for="op in adminOperators" :key="op.id" :value="op.id">
              {{ op.operatorName || op.email }}
            </option>
          </select>
        </div>
      </div>

      <!-- Stato consegna parziale -->
      <div
        v-if="isPartialDelivery && causale === 'consegna'"
        :class="[
          'ddt-partial-warning',
          ddtConsegnaCount > 0 ? 'ddt-partial-warning--continuation' : '',
        ]"
      >
        <i
          :class="
            ddtConsegnaCount > 0
              ? 'fa-solid fa-rotate'
              : 'fa-solid fa-circle-info'
          "
        ></i>
        <span v-if="ddtConsegnaCount === 0">
          Prima consegna parziale — le quantità mostrate sono quelle del
          preventivo. Modifica quelle che vuoi consegnare in questa spedizione.
        </span>
        <span v-else>
          {{ ddtConsegnaCount }}
          {{
            ddtConsegnaCount === 1
              ? "consegna già effettuata"
              : "consegne già effettuate"
          }}
          — le quantità mostrate sono il residuo da consegnare.
        </span>
      </div>

      <!-- Selezione articoli -->
      <div class="ddt-field">
        <label class="ddt-label">
          Articoli
          <span class="ddt-items-count"
            >({{ selectedCount }} /
            {{ selectionItems.length }} selezionati)</span
          >
        </label>
        <div class="ddt-items-preview">
          <table class="ddt-preview-table">
            <thead>
              <tr>
                <th class="ddt-col-check">
                  <input
                    type="checkbox"
                    :checked="allSelected"
                    :indeterminate.prop="someSelected && !allSelected"
                    @change="toggleAll"
                  />
                </th>
                <th>Descrizione</th>
                <th class="ddt-col-qty">Quantità</th>
                <th class="ddt-col-uom">UdM</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(it, i) in selectionItems"
                :key="i"
                :class="{
                  'ddt-row-disabled': !it.selected,
                  'ddt-row-exhausted': isItemExhausted(it),
                }"
              >
                <td class="ddt-col-check">
                  <input
                    type="checkbox"
                    v-model="it.selected"
                    :disabled="isItemExhausted(it)"
                  />
                </td>
                <td>{{ it.description }}</td>
                <td class="ddt-col-qty">
                  <template v-if="!isItemExhausted(it)">
                    <input
                      v-if="it.selected"
                      type="number"
                      class="ddt-qty-input"
                      v-model.number="it.editableQty"
                      min="1"
                      :max="it.quantity"
                      step="1"
                      @input="clampQty(it)"
                    />
                    <span v-else class="ddt-qty-muted">{{ it.quantity }}</span>
                    <span
                      v-if="
                        it.originalQty != null && it.originalQty !== it.quantity
                      "
                      class="ddt-qty-original"
                      :title="`Totale preventivo: ${it.originalQty}`"
                      >/ {{ Math.floor(it.originalQty) }} tot.</span
                    >
                  </template>
                  <span v-else class="ddt-qty-exhausted-label">
                    {{
                      causale === "ritiro"
                        ? "Già ritirato"
                        : causale === "reso"
                        ? "Non consegnato"
                        : "Già consegnato"
                    }}
                  </span>
                </td>
                <td class="ddt-col-uom">{{ it.uom }}</td>
              </tr>
              <tr v-if="selectionItems.length === 0">
                <td colspan="4" style="text-align: center; color: #888">
                  Nessun articolo.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        v-if="isPartialDelivery && causale === 'consegna'"
        class="ddt-last-delivery-row"
      >
        <label class="ddt-last-delivery-label">
          <input type="checkbox" v-model="isLastDelivery" />
          Ultima consegna (segna preventivo come "Consegnato")
        </label>
      </div>

      <div v-if="showRitiroRemovalWarning" class="ddt-ritiro-warning">
        <p class="ddt-ritiro-warning__text">
          <i class="fa-solid fa-triangle-exclamation"></i>
          Gli articoli deselezionati verranno rimossi dal ritiro e le relative
          quantità torneranno disponibili in magazzino. Confermi
          l'aggiornamento?
        </p>
        <div class="ddt-ritiro-warning__actions">
          <button
            class="btn btn-secondary"
            type="button"
            @click="showRitiroRemovalWarning = false"
          >
            Annulla
          </button>
          <button
            class="btn btn-danger"
            type="button"
            @click="
              showRitiroRemovalWarning = false;
              doGenerate();
            "
          >
            <i class="fa-solid fa-triangle-exclamation"></i> Conferma
            aggiornamento
          </button>
        </div>
      </div>

      <!-- Limite acconto -->
      <div
        v-if="showAccontoLimitInfo"
        :class="[
          'ddt-acconto-banner',
          isOverAccontoLimit
            ? 'ddt-acconto-banner--over'
            : 'ddt-acconto-banner--ok',
        ]"
      >
        <i
          :class="
            isOverAccontoLimit ? 'fa-solid fa-ban' : 'fa-solid fa-circle-info'
          "
        ></i>
        <span>
          Limite merce (50% acconto):
          <strong>{{ formatPriceToPrint(accontoLimit) }}</strong>
          <span v-if="alreadyDeliveredValue > 0">
            — già consegnato/ritirato:
            <strong>{{ formatPriceToPrint(alreadyDeliveredValue) }}</strong>
          </span>
          — selezionato:
          <strong>{{ formatPriceToPrint(selectedValue) }}</strong> — totale:
          <strong>{{ formatPriceToPrint(totalDdtValue) }}</strong>
          <!-- [sc-49300] cosa fare per sbloccare «Genera DDT» -->
          <span v-if="isOverAccontoLimit" class="ddt-acconto-banner__azione">
            <template v-if="margineAcconto > 0">
              Per generare il DDT togli articoli o riduci le quantità di almeno
              {{ formatPriceToPrint(totalDdtValue - accontoLimit) }}.
            </template>
            <template v-else>
              Il limite è già raggiunto con la merce consegnata: per consegnarne
              altra serve il saldo o un acconto più alto.
            </template>
          </span>
        </span>
      </div>

      <div class="ddt-modal__actions">
        <button
          class="btn btn-secondary"
          type="button"
          @click="$emit('update:show', false)"
        >
          Annulla
        </button>
        <button
          class="btn btn-primary"
          type="button"
          :disabled="
            !causale ||
            selectedCount === 0 ||
            busy ||
            showRitiroRemovalWarning ||
            isOverAccontoLimit
          "
          :title="
            isOverAccontoLimit
              ? `Valore totale (${formatPriceToPrint(
                  totalDdtValue
                )}) supera il limite acconto (${formatPriceToPrint(
                  accontoLimit
                )})`
              : undefined
          "
          @click="generate"
        >
          <span v-if="busy">Generazione…</span>
          <span v-else
            ><i class="fa-solid fa-file-arrow-down"></i> Genera DDT</span
          >
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from "@/axiosInstance";
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import { generateDdtPdf } from "@/utilities/generateDdtPdf";
import { formatPriceToPrint } from "@/utilities/formatting";
import { limiteMerceAcconto, valoreMerceMossa } from "@/utilities/pagamenti";

const CAUSALI = [
  { value: "consegna", label: "Consegna", icon: "fa-solid fa-truck" },
  { value: "ritiro", label: "Ritiro", icon: "fa-solid fa-box-open" },
  { value: "reso", label: "Reso", icon: "fa-solid fa-rotate-left" },
];

export default {
  name: "DdtModal",
  props: {
    show: { type: Boolean, required: true },
    items: { type: Array, default: () => [] },
    quoteId: { type: Number, default: null },
    clientId: { type: Number, default: null },
    clientName: { type: String, default: null },
    deferred: { type: Boolean, default: false },
    isPartialDelivery: { type: Boolean, default: false },
    ddtConsegnaCount: { type: Number, default: 0 },
    consegnaComplete: { type: Boolean, default: false },
    hasExistingRitiro: { type: Boolean, default: false },
    hasExistingReso: { type: Boolean, default: false },
    depositAmount: { type: Number, default: 0 },
    paymentState: { type: String, default: null },
    defaultMagazziniereId: { type: Number, default: null },
    defaultOperatoreId: { type: Number, default: null },
  },
  emits: ["update:show", "generated", "queued"],
  data() {
    return {
      CAUSALI,
      causale: null,
      magazziniereId: null,
      operatoreId: null,
      busy: false,
      selectionItems: [],
      isLastDelivery: false,
      showRitiroRemovalWarning: false,
    };
  },
  computed: {
    ...mapGetters({ operators: "operators/list" }),
    warehouseOperators() {
      return (this.operators || []).filter((op) => op.role === "magazziniere");
    },
    adminOperators() {
      return (this.operators || []).filter((op) => op.role !== "magazziniere");
    },
    selectedCount() {
      return this.selectionItems.filter((it) => it.selected).length;
    },
    selectableItems() {
      return this.selectionItems.filter((it) => !this.isItemExhausted(it));
    },
    allSelected() {
      return (
        this.selectableItems.length > 0 &&
        this.selectableItems.every((it) => it.selected)
      );
    },
    someSelected() {
      return this.selectableItems.some((it) => it.selected);
    },
    accontoLimit() {
      return limiteMerceAcconto(this.depositAmount);
    },
    // Quanta merce si può ancora consegnare entro il limite dell'acconto
    margineAcconto() {
      return this.accontoLimit - this.alreadyDeliveredValue;
    },
    alreadyDeliveredValue() {
      return valoreMerceMossa(this.selectionItems);
    },
    selectedValue() {
      return this.selectionItems
        .filter((it) => it.selected)
        .reduce((sum, it) => sum + it.editableQty * (it.price || 0), 0);
    },
    totalDdtValue() {
      return this.alreadyDeliveredValue + this.selectedValue;
    },
    showAccontoLimitInfo() {
      return (
        this.paymentState === "Pagato con Acconto" &&
        ["consegna", "ritiro"].includes(this.causale) &&
        this.depositAmount > 0
      );
    },
    isOverAccontoLimit() {
      return (
        this.showAccontoLimitInfo && this.totalDdtValue > this.accontoLimit
      );
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.fetchOperators().then(() => {
          this.magazziniereId = this.defaultMagazziniereId;
          this.operatoreId = this.defaultOperatoreId;
        });
        this.initSelection();
      } else {
        this.causale = null;
        this.magazziniereId = null;
        this.operatoreId = null;
        this.isLastDelivery = false;
      }
    },
    items() {
      this.initSelection();
    },
    causale(newVal, oldVal) {
      if (newVal === oldVal) return;
      if (newVal === "reso") {
        this.selectionItems.forEach((it) => {
          it.quantity = it.deliveredQty;
          it.editableQty = Math.floor(it.deliveredQty);
          it.selected = Math.floor(it.deliveredQty) > 0;
        });
      } else if (oldVal === "reso") {
        this.selectionItems.forEach((it) => {
          it.quantity = it.baseQty;
          it.editableQty = Math.floor(it.baseQty);
        });
      }
    },
  },
  methods: {
    ...mapActions(["fetchOperators"]),
    formatPriceToPrint,
    isCausaleDisabled(value) {
      if (value === "reso") return this.hasExistingReso;
      return this.consegnaComplete;
    },
    causaleDisabledTitle(value) {
      if (value === "reso" && this.hasExistingReso)
        return "Il reso è già stato effettuato per questo preventivo";
      if (value !== "reso" && this.consegnaComplete)
        return "Tutte le quantità del preventivo sono già state consegnate";
      return undefined;
    },
    clampQty(item) {
      const val = Math.floor(Number(item.editableQty));
      if (!val || val < 1) {
        item.editableQty = 1;
      } else if (val > item.quantity) {
        item.editableQty = Math.floor(item.quantity);
      } else {
        item.editableQty = val;
      }
    },
    isItemExhausted(it) {
      if (this.causale === "reso") return Math.floor(it.deliveredQty) <= 0;
      return Math.floor(it.quantity) <= 0;
    },
    initSelection() {
      this.selectionItems = this.items.map((it) => ({
        ...it,
        selected: Math.floor(it.quantity) > 0,
        editableQty: Math.floor(it.quantity),
        originalQty: it.originalQty ?? null,
        deliveredQty: it.deliveredQty ?? 0,
        movedQty: it.movedQty ?? 0,
        baseQty: it.quantity,
        price: it.price || 0,
      }));
    },
    toggleAll(e) {
      const val = e.target.checked;
      this.selectionItems.forEach((it) => {
        if (!this.isItemExhausted(it)) it.selected = val;
      });
    },
    async generate() {
      if (
        this.causale === "ritiro" &&
        this.hasExistingRitiro &&
        this.selectionItems.some((it) => !it.selected)
      ) {
        this.showRitiroRemovalWarning = true;
        return;
      }
      await this.doGenerate();
    },
    async doGenerate() {
      const activeItems = this.selectionItems
        .filter((it) => it.selected)
        .map((it) => ({
          itemId: it.itemId || null,
          description: it.description,
          quantity: Math.min(it.editableQty, it.quantity),
          uom: it.uom,
        }));
      if (!this.causale || activeItems.length === 0) return;

      if (this.deferred) {
        this.$emit("queued", {
          causale: this.causale,
          magazziniereId: this.magazziniereId,
          operatoreId: this.operatoreId,
          items: activeItems,
        });
        this.$toast.info(
          "DDT in coda. Verrà generato al salvataggio del preventivo."
        );
        this.$emit("update:show", false);
        return;
      }

      this.busy = true;
      try {
        const { data: ddt } = await axiosInstance.post("/ddt", {
          causale: this.causale,
          quoteId: this.quoteId,
          clientId: this.clientId,
          magazziniereId: this.magazziniereId,
          operatoreId: this.operatoreId,
          items: activeItems,
        });

        const blob = generateDdtPdf(ddt);
        const isDev =
          process.env.NODE_ENV === "development" ||
          import.meta?.env?.MODE === "development" ||
          process.env?.VUE_APP_NODE_ENV === "development";
        const withSuffixBeforeExt = (name, suffix) => {
          if (!suffix) return name;
          const dot = name.lastIndexOf(".");
          return dot <= 0
            ? `${name}.${suffix}`
            : `${name.slice(0, dot)}.${suffix}${name.slice(dot)}`;
        };
        const baseName = ddt.quoteId
          ? ddt.causale === "consegna"
            ? `DDT_${ddt.quoteId}_CONSEGNA_${ddt.sequence}.pdf`
            : `DDT_${ddt.quoteId}_${ddt.causale.toUpperCase()}.pdf`
          : `DDT_N_${ddt.id}.pdf`;
        const filename = withSuffixBeforeExt(baseName, isDev ? "dev" : "");

        // Carica su Minio
        const formData = new FormData();
        formData.append(
          "document",
          new File([blob], filename, { type: "application/pdf" })
        );
        await axiosInstance.post(`/upload/ddts/${ddt.id}/documents`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        // Scarica il PDF localmente
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);

        this.$toast.success(`DDT N° ${ddt.id} generato e salvato.`);
        this.$emit("generated", { ddt, isLastDelivery: this.isLastDelivery });
        this.$emit("update:show", false);
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Errore nella generazione del DDT."
        );
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.ddt-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}
.ddt-modal {
  background: #fff;
  border-radius: 8px;
  padding: 28px 32px;
  min-width: 380px;
  max-width: 560px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}

@media (max-width: 768px) {
  .ddt-modal {
    width: 85%;
    padding: 28px 12px;
  }
}
.ddt-modal__close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 8px;
  cursor: pointer;
}

.ddt-modal__close i {
  color: black;
}

.ddt-modal__close:hover {
  background: #ccc;
}

.ddt-modal__title {
  margin: 0 0 4px;
  font-size: 1.1rem;
}
.ddt-modal__subtitle {
  margin: 0 0 16px;
  color: #666;
  font-size: 0.9rem;
}
.ddt-field {
  margin-bottom: 18px;
}
.ddt-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 8px;
}
.ddt-causale-cards {
  display: flex;
  gap: 10px;
}
.ddt-causale-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 8px;
  border: 2px solid #ddd;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: border-color 0.15s, background 0.15s;
}
.ddt-causale-card.selected {
  border-color: #1a1a1a;
  background: #f5f5f5;
}
.ddt-causale-card--disabled,
.ddt-causale-card--disabled:hover {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}
.ddt-causale-card i {
  font-size: 18px;
}
.ddt-items-preview {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 20px;
}
.ddt-preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.ddt-preview-table th,
.ddt-preview-table td {
  padding: 7px 10px;
  text-align: left;
  border-bottom: 1px solid #eee;
}
.ddt-preview-table thead th {
  background: #f8f8f8;
  font-weight: 600;
}
.ddt-operators-row {
  display: flex;
  gap: 14px;
  margin-bottom: 18px;
}
.ddt-operators-row .ddt-field {
  flex: 1;
  margin-bottom: 0;
}
.ddt-select {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 13px;
}
.ddt-last-delivery-row {
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 6px;
}
.ddt-last-delivery-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.ddt-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.ddt-ritiro-warning {
  background: #fff8e1;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.ddt-ritiro-warning__text {
  margin: 0 0 10px;
  font-size: 0.875rem;
  color: #92400e;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.ddt-ritiro-warning__text .fa-triangle-exclamation {
  margin-top: 2px;
  flex-shrink: 0;
}
.ddt-ritiro-warning__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.ddt-items-count {
  font-weight: 400;
  color: #888;
  font-size: 0.8rem;
  margin-left: 4px;
}
.ddt-col-check {
  width: 32px;
  text-align: center !important;
  padding-left: 8px !important;
  padding-right: 8px !important;
}
.ddt-col-qty {
  width: 90px;
}
.ddt-col-uom {
  width: 52px;
}
.ddt-row-disabled td {
  opacity: 0.4;
}
.ddt-row-exhausted {
  background: #f9f9f9;
}
.ddt-row-exhausted td {
  opacity: 0.5;
}
.ddt-qty-exhausted-label {
  font-size: 11px;
  color: #888;
  font-style: italic;
  white-space: nowrap;
}
.ddt-qty-input {
  width: 76px;
  padding: 2px 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  text-align: right;
}
.ddt-qty-muted {
  color: #aaa;
  font-size: 13px;
}
.ddt-partial-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 6px;
  font-size: 13px;
  color: #1565c0;
}
.ddt-partial-warning--continuation {
  background: #fff8e1;
  border-color: #ffcc02;
  color: #7a5200;
}
.ddt-partial-warning i {
  margin-top: 1px;
  flex-shrink: 0;
}
.ddt-qty-original {
  display: block;
  font-size: 11px;
  color: #999;
  margin-top: 2px;
  white-space: nowrap;
}
.ddt-acconto-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 12px;
  line-height: 1.5;
}
.ddt-acconto-banner--ok {
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
}
.ddt-acconto-banner--over {
  background: #ffebee;
  color: #b71c1c;
  border: 1px solid #ef9a9a;
}
.ddt-acconto-banner__azione {
  display: block;
  margin-top: 4px;
  font-weight: 600;
}
.ddt-acconto-banner i {
  margin-top: 2px;
  flex-shrink: 0;
}
</style>
